import React, {
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useState,
} from 'react';
import { SmlsButton, SmlsCounterInput } from '@smiles/smiles-ui-kit-react';
import { useTranslation } from 'react-i18next';

import { useGeoLocation } from '../../hooks/useGeoLocation';
import { useSearch } from '../../hooks/search';
import SearchGroup from '../../components/SearchGroup';

import { getCounterItems } from '../../common/initialItems';
import { handleChangeCounter } from '../../common/searchUtils';

import SelectFlightType from '../../components/SelectFlightType';
import SelectFlightClass from '../../components/SelectFlightClass';
import CrossSell from '../../components/CrossSell';

type Props = {
  onDismiss?: VoidFunction;
};

const DesktopFlightSearch: React.FC<Props> = ({ onDismiss }) => {
  const [isCrossSellChecked, setIsCrossSellChecked] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const {
    setFlightPassagers,
    isLoading,
    submitSearch,
    hasSomeSearchError,
    isMultiCityFlight,
    flightPassagers,
  } = useSearch();

  const { handleGetCurrentGeoLocation } = useGeoLocation();

  const { t } = useTranslation('flightSearch');

  const counterItems = useMemo(
    () =>
      getCounterItems({
        t,
        adultsValue: flightPassagers.adults,
        childrenValue: flightPassagers.children,
        babiesValue: flightPassagers.babies,
      }),
    [flightPassagers, t]
  );

  /* istanbul ignore next */
  const changeZIndexFooterElement = (value: string) => {
    const footerElement = document.getElementById('smls-hf-footer-default');
    if (footerElement) {
      footerElement.style.zIndex = value;
    }
  };

  const removeBodyClass = (className: string) => {
    const bodyElement = document.querySelector('body');
    bodyElement?.classList.remove(className);
  };

  const addBodyClass = (className: string) => {
    const bodyElement = document.querySelector('body');
    bodyElement?.classList.add(className);
  };

  const handleFormClick = (): void => {
    if (formRef.current) {
      const pos = formRef.current.style.position;
      const { top } = formRef.current.style;
      addBodyClass('backdropFlightSearch');
      addBodyClass('overflowHidden');
      addBodyClass('active');
      formRef.current.style.position = 'relative';
      formRef.current.style.top = '-40px';
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      formRef.current.style.top = top;
      formRef.current.style.position = pos;

      changeZIndexFooterElement('1');
    }
  };

  /* istanbul ignore next */
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (!formRef?.current?.contains(event.target as Node)) {
        removeBodyClass('active');
        removeBodyClass('backdropFlightSearch');
        removeBodyClass('overflowHidden');
        removeBodyClass('multiCityFlight');
        changeZIndexFooterElement('1000');
        onDismiss?.();
      }
    },
    [onDismiss]
  );

  useEffect(() => {
    handleGetCurrentGeoLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    isMultiCityFlight
      ? addBodyClass('multiCityFlight')
      : removeBodyClass('multiCityFlight');
  }, [isMultiCityFlight]);

  const isLocalhost =
    window.location.hostname.indexOf('localhost') >= 0 ? 'localhost' : '';

  return (
    <>
      <section className={`smls-flight-search-desktop ${isLocalhost}`}>
        <form
          ref={formRef}
          id="DesktopFlightSearch-form-click"
          className="wrapper"
          onSubmit={(e) => {
            e.preventDefault();
            if (!isLoading) {
              console.log(
                'chamada: smiles-react-flight-search submit form ->  submitSearch()'
              );
              submitSearch();
            }
          }}
          onClick={handleFormClick}
        >
          <header className="header">
            {' '}
            <div className="title">
              {' '}
              <span className="primary">Buscar</span>{' '}
              <svg
                width="17"
                height="5"
                viewBox="0 0 17 5"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                {' '}
                <path
                  d="M16.6071 0.897797C16.6045 0.880414 16.5927 0.866381 16.5765 0.861756C16.5597 0.856015 16.5422 0.861437 16.5304 0.873876C16.5304 0.873876 16.5203 0.885039 16.4993 0.906249C16.4781 0.928097 16.4463 0.959991 16.4036 1.00098C16.3188 1.08278 16.1917 1.19936 16.0225 1.33969C15.9024 1.43936 15.7616 1.55068 15.5995 1.67044C15.5994 1.66741 15.5987 1.6647 15.5986 1.66167C13.6302 3.14109 11.3158 3.80338 9.05607 3.86207C9.03694 3.8627 9.01732 3.8627 8.99819 3.86318C8.93488 3.86462 8.87141 3.86589 8.80809 3.86621C8.7459 3.86701 8.68482 3.86924 8.62183 3.86924V3.8694V3.86924C6.41745 3.86924 4.65416 3.32273 3.35638 2.69632C2.42411 2.25841 1.60027 1.71429 0.937187 1.09156C0.901147 1.05839 0.867657 1.02713 0.840388 1.00082C0.797968 0.959831 0.766233 0.927937 0.745023 0.906089C0.724132 0.88472 0.714086 0.873717 0.713767 0.873717C0.702125 0.861278 0.684105 0.856015 0.667998 0.861597C0.651732 0.866221 0.639772 0.880255 0.63738 0.897637C0.628768 0.961267 0.62207 1.04212 0.62207 1.13573V1.15726V1.16396C0.626854 1.40588 0.708026 1.59246 0.926343 1.79626C0.979128 1.85048 3.91358 4.85781 8.62199 4.85877V4.85797V4.85877C13.3304 4.85797 16.2652 1.85048 16.318 1.79626C16.5364 1.59246 16.6176 1.40572 16.6221 1.16396V1.15726V1.13573C16.6219 1.04212 16.616 0.961267 16.6071 0.897797Z"
                  fill="#666666"
                />{' '}
              </svg>{' '}
              <span className="secondary"> Voos </span>{' '}
            </div>{' '}
          </header>
          <div className="top-content">
            <SelectFlightType />

            <SmlsCounterInput
              min={1}
              max={9}
              mobileTitleModal={t('counter.mobileTitleModal')}
              mobileSubtitleModal={t('counter.mobileSubtitleModal')}
              mobileLabel={t('counter.mobileLabel')}
              arrayOfItems={counterItems}
              confirmButtonId="btn_confirmPassagers"
              desktopButtonId="drop_passagers"
              icon="person"
              submitForm={(counterValues) => {
                if (counterValues) {
                  const passagers = handleChangeCounter(counterValues);
                  setFlightPassagers(passagers);
                }
              }}
            />

            <SelectFlightClass />
            <CrossSell
              isChecked={isCrossSellChecked}
              onChecked={setIsCrossSellChecked}
            />
          </div>
          <div className="bottom-content">
            <div className="list-of-destinations">
              <SearchGroup />
            </div>
            <SmlsButton
              id="btn_search"
              type="submit"
              className={[
                hasSomeSearchError ? 'expand-submit-button' : '',
                isMultiCityFlight ? 'align-submit-bottom' : '',
              ].join(' ')}
              text={t('submitButton')}
              isLoading={isLoading}
              onClick={() => submitSearch({ isCrossSellChecked })}
            />
          </div>
        </form>
      </section>
    </>
  );
};

DesktopFlightSearch.defaultProps = {
  onDismiss: () => {
    // noop
  },
};

export default DesktopFlightSearch;
